import { useEffect, useRef } from "react";
import type { Product } from "@/mocks/products";
import ProductCard from "./ProductCard";

interface ProductListingProps {
  products: Product[];
  hasMore: boolean;
  onLoadMore: () => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  loading?: boolean;
  error?: string;
  onRetry?: () => void;
}

export default function ProductListing({ products, hasMore, onLoadMore, hasActiveFilters, onClearFilters, loading = false, error = "", onRetry }: ProductListingProps) {
  const waiting = loading && products.length === 0;
  const failed = Boolean(error) && products.length === 0;
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) onLoadMore();
      },
      { rootMargin: "600px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  if (failed) {
    return (
      <div role="alert" className="bz-empty">
        <p>{error}</p>
        {onRetry && <button type="button" className="bz-link" onClick={onRetry}>Tentar novamente</button>}
      </div>
    );
  }

  if (!waiting && products.length === 0) {
    return (
      <div className="bz-empty">
        <p>Nenhuma peça encontrada{hasActiveFilters ? " com esses filtros" : ""}.</p>
        {hasActiveFilters && <button type="button" className="bz-link" onClick={onClearFilters}>Limpar filtros</button>}
      </div>
    );
  }

  return (
    <>
      <div className="bz-grid">
        {waiting
          ? Array.from({ length: 8 }, (_, index) => <div key={index} className="bz-skeleton" aria-hidden="true" />)
          : products.map((product) => <ProductCard key={product.id} product={product} />)}
      </div>
      {hasMore && <div ref={sentinelRef} className="bz-more" role="status">Carregando mais peças...</div>}
    </>
  );
}
